/**
 * Chess Engine Error Classes
 * Provides specific error types with user-friendly messages and recovery actions
 */

export abstract class EngineError extends Error {
  abstract readonly userMessage: string;
  abstract readonly recoveryAction: string;
  abstract readonly canRetry: boolean;
  
  constructor(
    message: string,
    public readonly originalError?: unknown
  ) {
    super(message);
    this.name = this.constructor.name;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export class TimeoutError extends EngineError {
  readonly userMessage = "Analysis took too long";
  readonly recoveryAction = "Try a simpler position or refresh the page";
  readonly canRetry = true;

  constructor(timeoutMs: number, originalError?: unknown) {
    super(`Analysis timeout after ${timeoutMs}ms`, originalError);
  }
}

export class WorkerCrashError extends EngineError {
  readonly userMessage = "Chess engine crashed";
  readonly recoveryAction = "Engine will restart automatically";
  readonly canRetry = true;

  constructor(originalError?: unknown) {
    super('Stockfish worker crashed unexpectedly', originalError);
  }
}

export class NetworkError extends EngineError {
  readonly userMessage = "Cloud analysis unavailable";
  readonly recoveryAction = "Check your internet connection and try again";
  readonly canRetry = true;

  constructor(originalError?: unknown) {
    super('Failed to connect to cloud analysis service', originalError);
  }
}

export class ResourceExhaustedError extends EngineError {
  readonly userMessage = "Too many analysis requests";
  readonly recoveryAction = "Please wait a few seconds and try again";
  readonly canRetry = true;

  constructor(public readonly retryAfterMs: number = 5000, originalError?: unknown) {
    super(`Rate limit exceeded, retry after ${retryAfterMs}ms`, originalError);
  }
}

export class InvalidPositionError extends EngineError {
  readonly userMessage = "Invalid chess position";
  readonly recoveryAction = "Reset the board or load a valid position";
  readonly canRetry = false;

  constructor(fen: string, originalError?: unknown) {
    super(`Invalid FEN: ${fen}`, originalError);
  }
}

export class EngineNotReadyError extends EngineError {
  readonly userMessage = "Chess engine not ready";
  readonly recoveryAction = "Please wait for the engine to initialize";
  readonly canRetry = true;

  constructor(originalError?: unknown) {
    super('Engine initialization incomplete', originalError);
  }
}

/**
 * Parse error to determine if it's a specific engine error type
 */
export function classifyError(error: unknown): EngineError {
  if (error instanceof EngineError) {
    return error;
  }

  const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();

  // Classify based on error message patterns
  if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
    return new TimeoutError(30000, error);
  }

  if (errorMessage.includes('worker') || errorMessage.includes('crash') || errorMessage.includes('terminated')) {
    return new WorkerCrashError(error);
  }

  if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('offline')) {
    return new NetworkError(error);
  }

  if (errorMessage.includes('rate limit') || errorMessage.includes('too many requests') || errorMessage.includes('429')) {
    return new ResourceExhaustedError(5000, error);
  }

  if (errorMessage.includes('invalid fen') || errorMessage.includes('invalid position')) {
    return new InvalidPositionError('', error);
  }

  if (errorMessage.includes('not ready') || errorMessage.includes('not initialized')) {
    return new EngineNotReadyError(error);
  }

  // Default to generic timeout error for unknown errors
  return new TimeoutError(30000, error);
}

/**
 * Calculate exponential backoff delay
 */
export function getBackoffDelay(attempt: number, baseDelay: number = 500): number {
  return Math.min(baseDelay * Math.pow(2, attempt), 10000);
}
